// The zone every time is shown in: the sites' local time as the controller
// sets it (/auth/me time_zone, default Europe/Berlin), the same for everyone
// - people who talk about a time mean the same one; the browser's zone until
// it is known. The top bar says when it is not the viewer's own.
let zone: string | undefined

export function setDisplayZone(z?: string) {
  zone = z || undefined
}

export function displayZone(): string {
  return zone || Intl.DateTimeFormat().resolvedOptions().timeZone
}

// zoneOffsetMinutes: the zone's offset to UTC at the time t, in minutes.
export function zoneOffsetMinutes(z: string, t = new Date()): number {
  const part = new Intl.DateTimeFormat('en-US', { timeZone: z, timeZoneName: 'longOffset' }).formatToParts(t).find((p) => p.type === 'timeZoneName')?.value || 'GMT'
  const m = /GMT([+-])(\d{2}):?(\d{2})?/.exec(part)
  if (!m) return 0
  return (m[1] === '-' ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3] || 0))
}

// Dates and times the same everywhere: 2026-10-19 01:46 (24 h, the display
// zone), unambiguous for everyone.
export function fmtTime(v?: string | number | Date | null): string {
  if (!v) return ''
  const d = new Date(v)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString('sv-SE', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: zone })
}

export function fmtDate(v?: string | number | Date | null): string {
  return fmtTime(v).slice(0, 10)
}

export function fmtTimeSec(v?: string | number | Date | null): string {
  if (!v) return ''
  const d = new Date(v)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString('sv-SE', { timeZone: zone })
}

// fmtAgo: a short relative time (5 min ago, 3 h ago, 2 d ago)
export function fmtAgo(v?: string | number | Date | null): string {
  if (!v) return ''
  const s = (Date.now() - new Date(v).getTime()) / 1000
  if (s < 90) return 'just now'
  if (s < 3600) return `${Math.round(s / 60)} min ago`
  if (s < 86400 * 2) return `${Math.round(s / 3600)} h ago`
  return `${Math.round(s / 86400)} d ago`
}

// fmtPower: watts for people (850 W, 3.45 kW)
export function fmtPower(w?: number | null): string {
  if (w === undefined || w === null) return '—'
  return w >= 1000 ? `${(w / 1000).toFixed(2)} kW` : `${Math.round(w)} W`
}

// fmtBps: a data rate for people (950 Mbit/s, 1.2 Gbit/s)
export function fmtBps(bps?: number | null): string {
  if (bps === undefined || bps === null) return '—'
  if (bps >= 1e9) return `${(bps / 1e9).toFixed(1)} Gbit/s`
  if (bps >= 1e6) return `${Math.round(bps / 1e6)} Mbit/s`
  return `${Math.round(bps / 1e3)} kbit/s`
}

// fmtWhen: a coming time by its day (today 22:00, tomorrow 22:00, else the date)
export function fmtWhen(v?: string | null): string {
  if (!v) return '—'
  const when = fmtTime(v)
  const clock = when.slice(-5)
  if (fmtDate(v) === fmtDate(new Date())) return `today ${clock}`
  if (fmtDate(v) === fmtDate(new Date(Date.now() + 24 * 3600 * 1000))) return `tomorrow ${clock}`
  return when
}
